import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { useSession } from '../../api/session';
import { t } from '../../i18n';
import {
  Action,
  Card,
  CardList,
  Code,
  ErrorState,
  Gap,
  Gutter,
  LoadingState,
  Muted,
  Section,
  SectionTitle,
  StatusBadge,
} from '../../shared/ui';
import { ConfirmDialog, Notice, canAct, formatDateTime } from '../review/staffKit';

/** Админские настройки: сейчас это два выключателя из набора сервера. */
export function AdminConfigSection({ active }: { active: boolean }) {
  const session = useSession();
  return <ConfigSettings active={active} writable={canAct(session)} />;
}

/** Ключи набора. Зеркало `ConfigKey` из apps/api/src/modules/app-config/constants.ts. */
type ConfigKey = 'maintenanceMode' | 'selfRegistration';

/** Ответ GET /api/app-config: настройки набора, по строке на ключ. */
interface ConfigResponse {
  items: Array<{
    key: string;
    description: string;
    value: unknown;
    isDefault: boolean;
    updatedAt: string | null;
  }>;
}

/**
 * Подписи выключателя. Своё у настройки только это: карточка, подтверждение и
 * запрос у них общие — второй экземпляр той же карточки под новый ключ
 * разъехался бы с первым на первой же правке.
 */
interface FlagTexts {
  title: string;
  state: { on: string; off: string };
  hint: string;
  action: { enable: string; disable: string };
  confirm: {
    enableTitle: string;
    disableTitle: string;
    enableText: string;
    disableText: string;
    enable: string;
    disable: string;
  };
}

function flagTexts(name: 'maintenance' | 'selfRegistration'): FlagTexts {
  return {
    title: t(`config.flag.${name}.title`),
    state: {
      on: t(`config.flag.${name}.state.on`),
      off: t(`config.flag.${name}.state.off`),
    },
    hint: t(`config.flag.${name}.hint`),
    action: {
      enable: t(`config.flag.${name}.action.enable`),
      disable: t(`config.flag.${name}.action.disable`),
    },
    confirm: {
      enableTitle: t(`config.flag.${name}.confirm.enableTitle`),
      disableTitle: t(`config.flag.${name}.confirm.disableTitle`),
      enableText: t(`config.flag.${name}.confirm.enableText`),
      disableText: t(`config.flag.${name}.confirm.disableText`),
      enable: t(`config.flag.${name}.confirm.enable`),
      disable: t(`config.flag.${name}.confirm.disable`),
    },
  };
}

/** Порядок на экране: сначала то, что закрывает приложение, потом то, что открывает. */
const FLAGS: readonly { key: ConfigKey; texts: FlagTexts }[] = [
  { key: 'maintenanceMode', texts: flagTexts('maintenance') },
  { key: 'selfRegistration', texts: flagTexts('selfRegistration') },
];

function ConfigSettings({ active, writable }: { active: boolean; writable: boolean }) {
  const config = useLoader<ConfigResponse>(
    (signal) => api.get<ConfigResponse>('/api/app-config', undefined, signal),
    [],
    active,
  );

  if (config.loading && !config.data) return <LoadingState />;
  if (config.error && !config.data) {
    return <ErrorState message={config.error} onRetry={config.reload} />;
  }
  if (!config.data) return null;

  const rows = config.data.items;

  return (
    <>
      {FLAGS.map(({ key, texts }) => {
        const row = rows.find((item) => item.key === key) ?? null;
        return (
          <FlagCard
            key={key}
            configKey={key}
            texts={texts}
            enabled={row?.value === true}
            isDefault={row?.isDefault ?? true}
            updatedAt={row?.updatedAt ?? null}
            writable={writable}
            onSaved={config.reload}
          />
        );
      })}
    </>
  );
}

/* ── Выключатель ─────────────────────────────────────────────────────────── */

/**
 * Один выключатель набора: состояние, сырое значение ключа, объяснение и
 * кнопка с подтверждением. Громкая кнопка всегда у ВКЛЮЧЕНИЯ: включают режим
 * работ и открывают запись — это и есть шаг, после которого что-то меняется
 * для всех сразу.
 */
function FlagCard({
  configKey,
  texts,
  enabled,
  isDefault,
  updatedAt,
  writable,
  onSaved,
}: {
  configKey: ConfigKey;
  texts: FlagTexts;
  enabled: boolean;
  isDefault: boolean;
  updatedAt: string | null;
  writable: boolean;
  onSaved: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/app-config/${configKey}`, { value: !enabled });
      setAsking(false);
      onSaved();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section>
      <SectionTitle>{texts.title}</SectionTitle>

      <CardList>
        <Card tone={enabled ? 'pending' : 'default'}>
          <StatusBadge tone={enabled ? 'warning' : 'published'}>
            {enabled ? texts.state.on : texts.state.off}
          </StatusBadge>

          {/* Сырой ключ со значением: переключатель один, а спрашивают всегда
              про строку в базе — стоит ли она вообще и что в ней лежит. */}
          <Gap step="s2h" />
          <Code tone="violet" note={isDefault ? t('config.valueDefault') : undefined}>
            {configKey} = {String(enabled)}
          </Code>

          <Gap step="s2h" />
          <Muted>{texts.hint}</Muted>

          <Gap step="s3" />
          <Action
            tone={enabled ? 'accent' : 'action'}
            size="m"
            stretched
            disabled={busy || !writable}
            onClick={() => setAsking(true)}
          >
            {enabled ? texts.action.disable : texts.action.enable}
          </Action>

          {updatedAt ? (
            <>
              <Gap step="s2h" />
              <Muted>{t('config.updatedAt', { date: formatDateTime(updatedAt) })}</Muted>
            </>
          ) : null}
        </Card>
      </CardList>

      {error ? (
        <Gutter>
          <Notice tone="error" title={t('config.error')}>
            {error}
          </Notice>
        </Gutter>
      ) : null}

      {asking ? (
        <ConfirmDialog
          title={enabled ? texts.confirm.disableTitle : texts.confirm.enableTitle}
          text={enabled ? texts.confirm.disableText : texts.confirm.enableText}
          confirmText={enabled ? texts.confirm.disable : texts.confirm.enable}
          destructive={!enabled}
          busy={busy}
          error={error}
          onCancel={() => setAsking(false)}
          onConfirm={() => void apply()}
        />
      ) : null}
    </Section>
  );
}
